import { inject, Injectable } from '@angular/core';
import { map } from 'rxjs';
import { ConstructAPI } from 'src/app/API/constructAPI';
import { RestApiService } from 'src/app/API/restapi';
import { FormJScheme } from 'src/app/constants';
import { FileInterface } from 'src/model/file.interface';
import { Docuseal, FormJ, IAPIFormJ } from 'src/model/formJ.model';
import { PaymentSchedule } from 'src/model/paymentSchedule.model';
import { FileAdapter, FileUrlBuilder } from './file.service';

interface CustFormJOptions {
	hasLoanGroup?: boolean;
	scheme?: FormJScheme;
	stamped?: boolean;
	createdAfter?: Date;
	createdBefore?: Date;
	stampedAfter?: Date;
	stampedBefore?: Date;
}

type FormJOptions = CustFormJOptions & {
	businessId?: number;
	branchId?: number;
	licenseId?: number;
};



@Injectable({
	providedIn: 'root'
})
class FormJAdapter {
	adaptPaymentSchedule(item: any): PaymentSchedule {
		return {
			monthsCount: item.monthsCount,
			amount: item.amount
		} as PaymentSchedule;
	}

	adapt(item: any): FormJ {
		return new FormJ(
			item.id,
			item.principal,
			item.customerId,
			item.customerName,
			item.icNumber,
			item.term,
			new Date(item.dateCreated),
			item.data.lastPayment,
			item.data.paymentSchedule.map((item: any) => this.adaptPaymentSchedule(item)),
			item.data.receiptNumber,
			item.data.stampDate && new Date(item.data.stampDate),
			item.scheme,
			item.businessId,
			item.businessBranchId,
			item.businessLicenseId,
			item.loanGroupId,
			item.data.version,
			item.dateStamped && new Date(item.dateStamped),
			item.docuseal_id,
			item.docuseal_link
		);
	}

	toApiModel(formJ: FormJ): IAPIFormJ {
		return {
			principal: formJ.Principal,
			customerid: formJ.CustomerId,
			term: formJ.Term,
			data: {
				paymentSchedule: formJ.PaymentSchedule,
				lastPayment: formJ.LastPayment,
				receiptNumber: formJ.ReceiptNumber,
				stampDate: formJ.Date?.toISOString(),
				version: formJ.Version
			},
			scheme: formJ.Scheme,
			businessLicenseId: formJ.LicenseId
		};
	}
}

@Injectable({
	providedIn: 'root'
})
export class FormJService {
	private api = inject(RestApiService);
	private constructApi = inject(ConstructAPI);
	private adapter = inject(FormJAdapter);
	private fileAdapter = inject(FileAdapter);
	private fileUrl = inject(FileUrlBuilder);

	getCustomerFormJs(customerId: number, o?: CustFormJOptions): Promise<FormJ[]> {
		return new Promise<FormJ[]>((resolve, reject) => {
			this.api
				.get<FormJ[]>(this.constructApi.getCustomerFormJs(customerId, o?.hasLoanGroup, o?.scheme))
				.pipe(map(data => data.map(item => this.adapter.adapt(item))))
				.subscribe({
					next: data => {
						resolve(data);
					},
					error: error => {
						this.api.handleError(error);
						reject(error);
					}
				});
		});
	}

	getCustomerFormJsHasFiles(companyId: number, customerId: number): Promise<number[]> {
		return new Promise<number[]>((resolve, reject) => {
			this.api
				.get<FormJ[]>(this.constructApi.getCutsomerFormJsHasFiles(companyId, customerId))
				.pipe(map(data => data['ids']))
				.subscribe({
					next: data => {
						resolve(data);
					},
					error: error => {
						this.api.handleError(error);
						reject(error);
					}
				});
		});
	}

	getAllFormJs(o?: FormJOptions): Promise<FormJ[]> {
		return new Promise<FormJ[]>((resolve, reject) => {
			this.api
				.get<FormJ[]>(
					this.constructApi.getAllFormJs(
						o?.scheme,
						o?.hasLoanGroup,
						o?.businessId,
						o?.branchId,
						o?.licenseId,
						o?.stamped,
						o?.createdAfter,
						o?.createdBefore,
						o?.stampedAfter,
						o?.stampedBefore
					)
				)
				.pipe(map(data => data.map(d => this.adapter.adapt(d))))
				.subscribe({
					next: data => resolve(data),
					error: error => {
						this.api.handleError(error);
						reject(error);
					}
				});
		});
	}

	getFormJDetails(id: number): Promise<FormJ> {
		return new Promise<FormJ>((resolve, reject) => {
			this.api
				.get<FormJ>(this.constructApi.getFormJDetails(id))
				.pipe(map(data => this.adapter.adapt(data)))
				.subscribe({
					next: data => {
						resolve(data);
					},
					error: error => {
						this.api.handleError(error);
						reject(error);
					}
				});
		});
	}

	postAddFormJ(formJ: FormJ): Promise<FormJ> {
		return new Promise<FormJ>((resolve, reject) => {
			this.api.post<FormJ>(this.constructApi.getAddFormJ(), this.adapter.toApiModel(formJ)).subscribe({
				next: data => resolve(this.adapter.adapt(data)),
				error: error => {
					this.api.handleError(error);
					reject(error);
				}
			});
		});
	}

	unstampFormJ(formJId: number): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			this.api.post<FormJ>(this.constructApi.getUnstampFormJ(formJId)).subscribe({
				next: () => resolve(),
				error: error => {
					this.api.handleError(error);
					reject(error);
				}
			});
		});
	}

	deleteFormJ(id: number) {
		return new Promise<void>((resolve, reject) => {
			this.api.delete(this.constructApi.getDeleteFormJ(id)).subscribe({
				next: () => resolve(),
				error: error => {
					this.api.handleError(error);
					reject(error);
				}
			});
		});
	}

	postStampFormJ(id: number, stampDate: Date) {
		const data = { dateStamped: stampDate.toISOString() };
		return new Promise<void>((resolve, reject) => {
			this.api.post(this.constructApi.getStampFormJ(id), data).subscribe({
				next: () => resolve(),
				error: error => {
					this.api.handleError(error);
					reject(error);
				}
			});
		});
	}

	getCustomerFormJFiles(customerId: number, formJId: number): Promise<FileInterface[]> {
		return new Promise<FileInterface[]>((resolve, reject) => {
			this.api
				.get<FileInterface[]>(this.constructApi.getCustomerStampedFormJFiles(customerId, formJId))
				.pipe(map(data => data.map(d => this.fileAdapter.adapt(d, this.fileUrl.buildCustomerFileUrl(customerId)))))
				.subscribe({
					next: data => resolve(data),
					error: error => {
						this.api.handleError(error);
						reject(error);
					}
				});
		});
	}

	// getHasStampedFormJ(customerId: number): Promise<boolean> {
	// return new Promise<boolean>((resolve, reject) => {
	//     this.api.get<boolean>(this.constructApi.getHasStampFormJ(customerId)).subscribe({
	//         next: (data) => resolve(data),
	//         error: (error) => {
	//             this.api.handleError(error);
	//             reject(error);
	//         }
	//     });
	// });
	// }

	
}
